// Firestore board data layer — the Posture A replacement for the Realtime
// Database utils/firebase.js. A board is a resident; notes live at
// boards/{boardId}/messages/{id}.
//
// Functions take the Firestore `db` instance (from the app's Firebase init) and,
// for writes, the caller's tenancy context { tenantId, authorUid } read from the
// signed-in user's custom claims — the security rules require tenantId on every
// message. (See firestore.rules and firestore-tests/board.test.js, which assert
// these exact operations against the emulator + rules.)
//
// NOTE: part of the Phase 3 migration. NOT yet imported by the running app
// (which still uses the RTDB utils/firebase.js). The component swap — threading
// { tenantId, authorUid } from the auth context into writes — happens when the
// client auth is wired in.

import { collection, doc, query, where, onSnapshot, setDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

const messagesCol = (db, boardId) => collection(db, `boards/${boardId}/messages`);
const messageDoc = (db, boardId, id) => doc(db, `boards/${boardId}/messages/${id}`);

// Subscribe to a board's notes. Calls onItems with an array on every change and
// returns the unsubscribe function. onError surfaces permission/connection
// failures instead of swallowing them (a lesson from the RTDB version, whose
// silent listener failures were hard to diagnose).
//
// Pass tenantId for CAREGIVER subscriptions: the rules require a caregiver's
// list read to filter by tenantId (the read rule checks resource.data.tenantId
// rather than get()-ing the resident doc, which prod denies inside list rules).
// A DEVICE omits tenantId — its read is authorized by the token's board claim,
// so it reads the collection directly.
export function subscribeBoardMessages(db, boardId, onItems, onError, tenantId) {
  const base = messagesCol(db, boardId);
  const ref = tenantId ? query(base, where('tenantId', '==', tenantId)) : base;
  return onSnapshot(
    ref,
    (snap) => onItems(snap.docs.map((d) => d.data())),
    (err) => {
      if (onError) onError(err);
      else console.error('board subscribe failed:', err);
    }
  );
}

// Create or fully overwrite a note. The complete, validated shape is always
// written (the rules validate item/color/pos on every write), so a position-only
// drag rewrites the whole note.
export function writeBoardMessage(db, boardId, note, { tenantId, authorUid, authorName }) {
  const data = {
    id: note.id,
    item: note.item,
    color: note.color,
    pos: note.pos,
    tenantId,
    // Preserve the ORIGINAL poster across edits/drags (a later editor shouldn't
    // become the author); fall back to the current user when first created.
    authorUid: note.authorUid || authorUid,
    authorName: note.authorName || authorName || null,
    // Who made THIS write (for the activity log's edit attribution) — overwritten
    // on every write, so it reflects the most recent editor, not the poster.
    updatedBy: authorUid || null,
    updatedByName: authorName || null,
    // Timestamps power activity/recency in the ops usage dashboard. createdAt is
    // preserved across edits; updatedAt tracks the latest touch.
    createdAt: note.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  // Reminder fields (optional): actionAt = the event instant (epoch ms, anchored
  // to the resident's timezone at creation); warnMs = lead time before it that
  // the note starts pulsing. Written only when the note is a reminder, and
  // preserved across drags/edits because callers spread the existing note.
  if (note.actionAt != null) {
    const warnMs = note.warnMs != null ? note.warnMs : 30 * 60 * 1000;
    data.actionAt = note.actionAt;
    data.warnMs = warnMs;
    // notifyAt drives the reminder-due push (functions remindersTick). notified
    // starts false and is flipped by the server once fired; because this write
    // fully overwrites the doc, editing a reminder re-arms its notification.
    data.notifyAt = note.actionAt - warnMs;
    data.notified = false;
  }
  // Optional schedule (visibility window + recurrence): startAt = show-from
  // (epoch ms), endAt = show-until for one-time (null = open-ended / "Always"),
  // recur = once|daily|weekly, recurDays = [0..6] for weekly. Absent => the note
  // is persistent (always shown). Preserved across drags/edits (spread note).
  if (note.startAt != null) {
    data.startAt = note.startAt;
    data.endAt = note.endAt != null ? note.endAt : null;
    data.recur = note.recur || 'once';
    data.recurDays = Array.isArray(note.recurDays) ? note.recurDays : [];
    data.alert = !!note.alert; // pulse + notify the Care Circle when it appears
  }
  return setDoc(messageDoc(db, boardId, note.id), data);
}

export function deleteBoardItem(db, boardId, id) {
  return deleteDoc(messageDoc(db, boardId, id));
}

// Log a DELETE to the board's activity log, attributed to the caregiver who did
// it — Firestore deletes are anonymous, so the deleting client records this
// itself (create/edit are logged server-side by Cloud Functions triggers).
// Metadata only: type + who + when, never any content. Best-effort (never blocks).
export function logContentDelete(db, boardId, { tenantId, type, authorUid, authorName }) {
  if (!boardId || !tenantId || !authorUid) return Promise.resolve();
  return addDoc(collection(db, `boards/${boardId}/events`), {
    tenantId,
    ts: serverTimestamp(),
    category: 'content',
    type,
    action: 'deleted',
    actorUid: authorUid,
    actorName: authorName || null,
  }).catch(() => { /* logging is best-effort */ });
}
